import type React from "react";
import { useId } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BellRing, MailX } from "lucide-react";
import type { reportingPeriod } from "@duress/shared";
import { fmtInt } from "@/lib/format";
import { BRAND } from "@/lib/colors";
import { ChartTooltip, useChartTheme } from "@/lib/chart";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { BreakdownBars, type BreakdownBar } from "../dashboard/BreakdownBars";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { useDashboardView, type SelectedView } from "@/features/periods/usePeriods";

type TeamAlertData = reportingPeriod.TeamAlertData;
type TeamAlertStoreEntry = reportingPeriod.TeamAlertStoreEntry;
type TeamAlertStatusEntry = reportingPeriod.TeamAlertStatusEntry;
type TeamAlertUserEntry = reportingPeriod.TeamAlertUserEntry;

type RankedUser = TeamAlertUserEntry & { rank: number };

// Maps a team-alert lifecycle status to a semantic colour. Keyword-matched so
// label variations resolve. "Expired" check-ins are the concerning outcome.
function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes("expired")) return BRAND.red;
  if (s.includes("cancel")) return "hsl(0 0% 55%)";
  if (
    s.includes("complete") ||
    s.includes("resolved") ||
    s.includes("acknowledg") ||
    s.includes("closed")
  ) {
    return BRAND.green;
  }
  if (s.includes("active") || s.includes("open") || s.includes("pending")) return BRAND.blue;
  if (s.includes("test")) return "hsl(0 0% 55%)";
  return BRAND.blue;
}

function StatusBreakdown({ entries }: { entries: TeamAlertStatusEntry[] }): React.JSX.Element {
  const max = Math.max(...entries.map((e) => e.alerts), 1);
  const bars: BreakdownBar[] = [...entries]
    .sort((a, b) => b.alerts - a.alerts)
    .map((entry) => ({
      label: entry.status,
      display: fmtInt(entry.alerts),
      color: statusColor(entry.status),
      pct: (entry.alerts / max) * 100,
    }));

  return <BreakdownBars bars={bars} />;
}

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatTopDay(date: string): string {
  // date is "YYYY-MM-DD" from the processor's timeline bucket.
  const parts = date.split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return date;
  const abbr = MONTH_ABBR[month - 1] ?? String(month);
  return `${abbr} ${String(day)}`;
}

// ── Cell renderers ────────────────────────────────────────────────────────────

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const teamCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

const teamMutedCell = (v: unknown): React.ReactNode => (
  <span className="text-muted-foreground">{typeof v === "string" ? v : ""}</span>
);

const userCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

const rankCell = (v: unknown): React.ReactNode => (
  <span className="num text-muted-foreground">
    #{fmtInt(typeof v === "number" ? v : Number(v))}
  </span>
);

// ── Columns ──────────────────────────────────────────────────────────────────

const TEAM_COLUMNS: DataTableColumn<TeamAlertStoreEntry>[] = [
  { key: "store", label: "Team", render: teamCell },
  { key: "alerts", label: "Alerts", render: numCell },
];

const USER_COLUMNS: DataTableColumn<RankedUser>[] = [
  { key: "rank", label: "Rank", render: rankCell },
  { key: "user", label: "User", render: userCell },
  { key: "store", label: "Team", render: teamMutedCell },
  { key: "alerts", label: "Alerts", render: numCell },
];

function rankByAlerts(users: TeamAlertUserEntry[]): RankedUser[] {
  return [...users]
    .sort((a, b) => b.alerts - a.alerts)
    .map((u, i) => ({ ...u, rank: i + 1 }));
}

// ── Tile helpers ──────────────────────────────────────────────────────────────

function buildTiles(ta: TeamAlertData): KPITile[] {
  const firstPoint = ta.timeline[0];
  const topPoint = firstPoint
    ? ta.timeline.reduce((best, p) => (p.alerts > best.alerts ? p : best), firstPoint)
    : null;

  const topDayTile: KPITile = topPoint
    ? {
        label: "Top Day",
        value: `${formatTopDay(topPoint.date)} — ${fmtInt(topPoint.alerts)}`,
      }
    : {
        label: "Top Day",
        value: "—",
        sub: "no time data in source CSV",
      };

  // by_status is sorted descending, so the first entry is the dominant outcome.
  const topStatus = ta.by_status[0] ?? null;
  const topStatusTile: KPITile = topStatus
    ? {
        label: "Top Status",
        value: `${topStatus.status} — ${fmtInt(topStatus.alerts)}`,
      }
    : {
        label: "Top Status",
        value: "—",
        sub: "no status data in source CSV",
      };

  return [
    { label: "Total Alerts", value: fmtInt(ta.total_alerts) },
    topStatusTile,
    { label: "Active Teams", value: fmtInt(ta.by_store.length) },
    topDayTile,
  ];
}

// ── Pure presentational component ────────────────────────────────────────────

export interface TeamAlertActivityProps {
  data: reportingPeriod.DashboardData;
}

export function TeamAlertActivity({ data }: TeamAlertActivityProps): React.JSX.Element {
  const teamAlert = data.team_alert;
  const chart = useChartTheme();
  const timelineTitleId = useId();

  if (teamAlert === null) {
    return (
      <div data-team-alert-state="not-uploaded">
        <EmptyState
          icon={BellRing}
          body="This period has no team alert data uploaded. Re-upload the period with a team alert CSV to see this tab."
        />
      </div>
    );
  }

  if (teamAlert.total_alerts === 0) {
    return (
      <div data-team-alert-state="empty-csv">
        <EmptyState
          icon={MailX}
          body="A team alert CSV was uploaded but contained 0 alerts for this period."
        />
      </div>
    );
  }

  const tiles = buildTiles(teamAlert);
  const rankedUsers = rankByAlerts(teamAlert.by_user);

  return (
    <div className="space-y-6">
      <KPIGrid tiles={tiles} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
        {/* Timeline chart */}
        <div className="card-duress p-5">
          <p id={timelineTitleId} className="font-head font-semibold text-sm text-foreground mb-4">
            Alerts Over Time
          </p>
          {teamAlert.timeline.length === 0 ? (
            <div className="py-12 text-center" data-team-alert-timeline="empty">
              <p className="text-sm text-muted-foreground">
                No timeline data — team alert CSV is missing a time column.
              </p>
            </div>
          ) : (
            <div role="img" aria-labelledby={timelineTitleId}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart
                data={teamAlert.timeline}
                margin={{ left: 10, right: 20, top: 4, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="teamAlertArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND.blue} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={BRAND.blue} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                  stroke={chart.grid}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                  stroke={chart.grid}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: chart.grid }} />
                <Area
                  type="monotone"
                  dataKey="alerts"
                  name="Alerts"
                  stroke={BRAND.blue}
                  strokeWidth={2}
                  fill="url(#teamAlertArea)"
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Status breakdown — the headline figures highlighted to clients */}
        <div className="card-duress p-5" data-team-alert-panel="status">
          <p className="font-head font-semibold text-sm text-foreground mb-4">
            Alerts by Status
          </p>
          {teamAlert.by_status.length === 0 ? (
            <div className="py-12 text-center" data-team-alert-status="empty">
              <p className="text-sm text-muted-foreground">
                No status data — team alert CSV is missing a Status column.
              </p>
            </div>
          ) : (
            <StatusBreakdown entries={teamAlert.by_status} />
          )}
        </div>
      </div>

      {/* Alerts by team */}
      <div className="card-duress p-5">
        <p className="font-head font-semibold text-sm text-foreground mb-4">
          Alerts by Team
        </p>
        {teamAlert.by_store.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No team data in this period's team alert CSV.
            </p>
          </div>
        ) : (
          <DataTable<TeamAlertStoreEntry>
            columns={TEAM_COLUMNS}
            data={teamAlert.by_store}
            defaultSort={{ key: "alerts", dir: "desc" }}
            searchKeys={["store"]}
          />
        )}
      </div>

      {/* Top users by alerts */}
      <div className="card-duress p-5">
        <p className="font-head font-semibold text-sm text-foreground mb-4">
          Top Users by Alerts
        </p>
        {rankedUsers.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No user data — team alert CSV is missing a User Name column.
            </p>
          </div>
        ) : (
          <DataTable<RankedUser>
            columns={USER_COLUMNS}
            data={rankedUsers}
            defaultSort={{ key: "alerts", dir: "desc" }}
            searchKeys={["user", "store"]}
          />
        )}
      </div>

      {/* Methodology note */}
      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          Team grouping uses the <em>Team Name</em> column from the source CSV (falling back to{" "}
          <em>Team</em>, <em>Store</em>, <em>Location</em>, or <em>Site</em> if absent).{" "}
          <strong>Top Users by Alerts</strong> shows the top 10 users by alert count, with each
          user's most-common team alongside. The timeline buckets alerts by day from the time
          column. All rows in the source CSV count — unlike Emergency, there is no testing-event
          filter for team alerts.
        </p>
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

export function TeamAlertActivityTab({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return <TeamAlertActivity data={data} />;
}
